/**
 * Lead Details — Component RBAC Registry
 *
 * Source of truth for every component-level permission toggle inside the
 * lead-details page tree. Each key maps to a JSX block or behavior that was
 * previously gated by a hardcoded role / email / level check.
 *
 * Consumers:
 *   - src/config/routeTabConfig.js (CaseManagement.routes.lead-details.components)
 *   - src/modules/user_management/store/permissionsStore.js (useComponentPermission)
 *   - src/modules/user_management/RoutePermissionsWizard.jsx (RBAC UI)
 *   - migrations/YYYYMMDD_seed_lead_details_components.js (backfill)
 *
 * Shape of each entry:
 *   label          — user-facing string shown in the RBAC toggle UI
 *   group          — bucket for visual grouping (see COMPONENT_GROUPS below)
 *   defaultAllowed — value used when no DB row exists (and for new roles post-seed)
 *                    Sensitive toggles default to `false`; everything else `true`.
 *   description    — optional one-line hint for the RBAC UI tooltip
 *
 * Convention for keys:
 *   - kebab-case
 *   - dotted prefix for sub-component scoping if needed
 */

export const COMPONENT_GROUPS = {
  calling: 'Calling',
  'admin-tools': 'Admin Tools',
  'agent-tools': 'Agent Tools',
  'medical-workflow': 'Medical Workflow',
  'orders-rx': 'Orders & Prescription',
  subtabs: 'Sub-tabs',
};

export const LEAD_DETAILS_COMPONENTS = {
  // ─── A. Calling (4) ────────────────────────────────────────────────
  'exotel-call': {
    label: 'Exotel / Ameyo call button',
    group: 'calling',
    defaultAllowed: true,
    description: 'Enables the inline Exotel / Ameyo dialer in the engagement header',
  },
  'voitekk-integration': {
    label: 'Voitekk Integration',
    group: 'calling',
    defaultAllowed: true,
  },
  'book-chat-call': {
    label: 'Book Chat Call',
    group: 'calling',
    defaultAllowed: true,
  },
  'offline-booking-button': {
    label: 'Offline Booking button',
    group: 'calling',
    defaultAllowed: true,
    description:
      'Renders the ConsultDoctorCanvas button labelled "Offline-Booking" in the engagement header. Originally visible to SUPPORT and PC-access roles (lead_agent, chat, ivr, medical_rep, clinic_receptionist). Runtime isPcAccess bypass was removed — RBAC flag is now the sole decider.',
  },

  // ─── B. Admin Tools (4) ────────────────────────────────────────────
  'revamp-products-catalog': {
    label: 'Revamp Products Catalog button',
    group: 'admin-tools',
    defaultAllowed: true,
    description: 'Shows the Product Catalog drawer button. Disable per role from RBAC UI.',
  },
  'all-order-assign-modal': {
    label: 'All-Order Assign modal',
    group: 'admin-tools',
    defaultAllowed: true,
  },
  'edit-customer-info': {
    label: 'Edit Customer Information',
    group: 'admin-tools',
    defaultAllowed: true,
  },
  'task-list': {
    label: 'Task List (desktop + mobile)',
    group: 'admin-tools',
    defaultAllowed: true,
  },
  'order-details-admin-panel': {
    label: 'Order Details admin panel',
    group: 'admin-tools',
    defaultAllowed: true,
  },

  // ─── C. Agent Tools (3) ────────────────────────────────────────────
  'smart-assist': {
    label: 'Smart Assist',
    group: 'agent-tools',
    defaultAllowed: true,
    description: 'AI assistance panel. Currently email-allowlisted (SMART_ASSIST_ACCESS).',
  },
  'rewards-points': {
    label: 'Rewards Points',
    group: 'agent-tools',
    defaultAllowed: true,
  },
  'user-log': {
    label: 'User Log',
    group: 'agent-tools',
    defaultAllowed: true,
  },
  'back-button': {
    label: 'Back button (Lead Profile header)',
    group: 'agent-tools',
    defaultAllowed: true,
    description:
      'Back arrow in the lead-details / next-call header. Default ON for every role (incl. admin / super_admin) — turn OFF per role+level (e.g. medical_rep L1/L2/L4/L5, ivr_agent L1) to keep coaches on the customer profile.',
  },

  // ─── D. Medical Workflow (3) ───────────────────────────────────────
  'post-purchase-feedback': {
    label: 'Post-Purchase Feedback',
    group: 'medical-workflow',
    defaultAllowed: true,
    description: 'Clinic receptionist feature',
  },
  'hair-test-retake': {
    label: 'Hair Test retake',
    group: 'medical-workflow',
    defaultAllowed: true,
  },
  'diet-plan': {
    label: 'Diet Plan canvas',
    group: 'medical-workflow',
    defaultAllowed: true,
  },

  // ─── E. Sub-tabs (1) ───────────────────────────────────────────────
  'formulation-retake': {
    label: 'Formulation retake (in Consult sub-tab)',
    group: 'subtabs',
    defaultAllowed: true,
  },

  // ─── F. Orders & Prescription (9) ──────────────────────────────────
  'custom-discount': {
    label: 'Custom Discount',
    group: 'orders-rx',
    defaultAllowed: false,
    description:
      'Sensitive — default OFF. Controls the <CustomDiscount> value/percentage input in the Place Order drawer. Pre-migration: email allowlist + role !== MEDICAL_REP.',
  },
  'coupon-discount': {
    label: 'Coupon Discount',
    group: 'orders-rx',
    defaultAllowed: false,
    description:
      'Sensitive — default OFF. Controls the <LeadDiscount> discount-code picker in the Place Order drawer.',
  },
  'zero-value-order': {
    label: 'Zero-value Order',
    group: 'orders-rx',
    defaultAllowed: false,
    description: 'Sensitive — default OFF. Grant per role explicitly.',
  },
  'refund-order': {
    label: 'Refund Order action',
    group: 'orders-rx',
    defaultAllowed: false,
    description: 'Sensitive — default OFF. Grant per role explicitly.',
  },
  'shopify-order-link': {
    label: 'Shopify order link',
    group: 'orders-rx',
    defaultAllowed: true,
  },
  'shipping-billing-form': {
    label: 'Shipping / Billing address form',
    group: 'orders-rx',
    defaultAllowed: true,
  },
  'prescription-write': {
    label: 'Create / Edit Prescription',
    group: 'orders-rx',
    defaultAllowed: true,
  },
  'improvement-plan': {
    label: 'Improvement Plan (Scalp Image)',
    group: 'orders-rx',
    defaultAllowed: true,
  },
  'scalp-image-delete': {
    label: 'Delete scalp image',
    group: 'orders-rx',
    defaultAllowed: false,
    description: 'Sensitive — default OFF',
  },
  'view-scalpie-upload-link': {
    label: 'View scalpie upload link',
    group: 'orders-rx',
    defaultAllowed: false,
    description:
      'Sensitive — default OFF. Unmasks the scalp-image upload link in chat and message logs. ' +
      'The page behind it needs no login, so whoever holds the link can upload AS the customer. ' +
      'Grant only to ops who upload on a customer\'s behalf.',
  },

};

/**
 * Sensitive keys — default OFF. Must be granted per role in seed migration.
 * Kept as a separate export so the seed migration / UI can highlight these.
 */
export const SENSITIVE_COMPONENT_KEYS = Object.entries(LEAD_DETAILS_COMPONENTS)
  .filter(([, meta]) => meta.defaultAllowed === false)
  .map(([key]) => key);

/**
 * Helper — all components belonging to a specific group, in declaration order.
 */
export const componentsByGroup = () => {
  const byGroup = {};
  Object.keys(COMPONENT_GROUPS).forEach((g) => { byGroup[g] = []; });
  Object.entries(LEAD_DETAILS_COMPONENTS).forEach(([key, meta]) => {
    if (!byGroup[meta.group]) byGroup[meta.group] = [];
    byGroup[meta.group].push({ key, ...meta });
  });
  return byGroup;
};
